declare global {
  interface Window {
    __APP_URL__?: string;
    __API_BASE_URL__?: string;
    __WEB3FORMS_ACCESS_KEY__?: string;
  }
}

const fromBuild = import.meta.env.VITE_APP_URL;
const defaultUrl = "https://app.thyra-ai.com";

function isLocalDevHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1";
}

function isLocalAppOrigin(url: string): boolean {
  try {
    return isLocalDevHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

function inferAppOriginFromHostname(hostname: string): string | null {
  if (hostname === "thyra-ai.com" || hostname === "www.thyra-ai.com") {
    return "https://app.thyra-ai.com";
  }
  if (hostname === "app-dev.thyra-ai.com") {
    return "https://app-dev.thyra-ai.com";
  }
  return null;
}

function acceptAppOrigin(
  raw: string,
  allowLocalhostOrigin: boolean,
): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/\/$/, "");
  if (!allowLocalhostOrigin && isLocalAppOrigin(normalized)) return null;
  return normalized;
}

/** Main app origin (sign-up links). Prefers localhost:3000 when landing runs locally. */
export function getAppUrl(): string {
  if (
    typeof window !== "undefined" &&
    isLocalDevHost(window.location.hostname)
  ) {
    const runtime = window.__APP_URL__?.trim();
    if (runtime) return runtime.replace(/\/$/, "");
    return "http://localhost:3000";
  }

  const allowLocalhostOrigin = false;
  for (const raw of [
    typeof window !== "undefined" ? (window.__APP_URL__ || "").trim() : "",
    (fromBuild || "").trim(),
  ]) {
    const accepted = acceptAppOrigin(raw, allowLocalhostOrigin);
    if (accepted) return accepted;
  }

  if (typeof window !== "undefined") {
    const inferred = inferAppOriginFromHostname(window.location.hostname);
    if (inferred) return inferred;
  }

  return defaultUrl;
}

/** @deprecated Use getAppUrl() at call time (supports runtime config.js). */
export const APP_URL = getAppUrl();

const siteUrlBuild = import.meta.env.VITE_SITE_URL;
const defaultSiteUrl = "https://thyra-ai.com";

export const SITE_URL = (siteUrlBuild || defaultSiteUrl).replace(/\/$/, "");

export { getApiBaseUrl } from "./api-config";

/** Web3Forms access key (public in client bundle). Prefers runtime config.js (K8s/Doppler). */
export const WEB3FORMS_ACCESS_KEY = (() => {
  const runtime =
    typeof window !== "undefined"
      ? (window.__WEB3FORMS_ACCESS_KEY__ || "").trim()
      : "";
  const fromBuild = (import.meta.env.VITE_WEB3FORMS_ACCESS_KEY ?? "").trim();
  return runtime || fromBuild;
})();
